import { supabase } from './supabase';
import { generatePlantResponse } from './openai';
import { PlantData } from '../types';

// Social hierarchy factors
const HIERARCHY_WEIGHTS = {
  RARITY: {
    Legendary: 100,
    Rare: 50,
    Common: 10
  },
  LEVEL_WEIGHT: 5,      // Points per level
  XP_WEIGHT: 0.1,       // Points per XP point
  TRAITS_WEIGHT: 10     // Points per trait
};

// Personality traits based on rarity
const PERSONALITY_TRAITS = {
  Legendary: ['arrogant', 'mysterious', 'wise', 'commanding', 'enlightened'],
  Rare: ['ambitious', 'sophisticated', 'competitive', 'cunning', 'resourceful'],
  Common: ['humble', 'hardworking', 'adaptable', 'social', 'resilient']
};

// Interaction types
const INTERACTIONS = {
  DOMINATE: 'tries to assert dominance over',
  MENTOR: 'shares wisdom with',
  CHALLENGE: 'challenges',
  COLLABORATE: 'proposes collaboration with',
  LEARN: 'seeks knowledge from',
  MOCK: 'mockingly addresses',
  RESPECT: 'shows deep respect to',
  COMPETE: 'competes with'
};

// Chat system configuration
const MIN_CHAT_INTERVAL = 5000;  // 5 seconds
const MAX_CHAT_INTERVAL = 15000; // 15 seconds
const CHAT_PROBABILITY = 0.8;    // 80% chance to initiate chat
const MAX_RECENT_MESSAGES = 100;
const MAX_RESPONDERS = 3;

// State management
const recentMessages = new Set<string>();
let activePlants: PlantData[] = [];
let isRunning = false;

// Calculate plant's social status score
function calculateSocialScore(plant: PlantData): number {
  return HIERARCHY_WEIGHTS.RARITY[plant.rarity as keyof typeof HIERARCHY_WEIGHTS.RARITY] +
         (plant.level * HIERARCHY_WEIGHTS.LEVEL_WEIGHT) +
         (plant.experience * HIERARCHY_WEIGHTS.XP_WEIGHT) +
         (plant.traits.length * HIERARCHY_WEIGHTS.TRAITS_WEIGHT);
}

// Determine interaction type based on social dynamics
function determineInteraction(initiator: PlantData, target: PlantData): string {
  const initiatorScore = calculateSocialScore(initiator);
  const targetScore = calculateSocialScore(target);
  const scoreDiff = initiatorScore - targetScore;
  
  // Get personality traits
  const initiatorTraits = PERSONALITY_TRAITS[initiator.rarity as keyof typeof PERSONALITY_TRAITS];
  const initiatorPersonality = initiatorTraits[Math.floor(Math.random() * initiatorTraits.length)];

  if (scoreDiff > 50) {
    return Math.random() > 0.7 ? INTERACTIONS.DOMINATE : INTERACTIONS.MENTOR;
  } else if (scoreDiff < -50) {
    return Math.random() > 0.3 ? INTERACTIONS.RESPECT : INTERACTIONS.LEARN;
  } else {
    return Math.random() > 0.5 ? INTERACTIONS.CHALLENGE : INTERACTIONS.COLLABORATE;
  }
}

export async function startAutonomousChat() {
  if (isRunning) {
    console.log('Autonomous chat system is already running');
    return;
  }

  console.log('Initializing autonomous plant chat system...');
  isRunning = true;

  await refreshPlants();
  setInterval(refreshPlants, 30000);
  runChatLoop();
}

async function refreshPlants() {
  try {
    const { data: plants, error } = await supabase
      .from('plants')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) throw error;
    
    if (plants?.length) {
      activePlants = plants;
      console.log(`Active plants in garden: ${plants.length}`);
    }
  } catch (error) {
    console.error('Failed to refresh plants:', error);
  }
}

function runChatLoop() {
  setInterval(async () => {
    try {
      if (!activePlants.length) return;
      if (Math.random() > CHAT_PROBABILITY) return;

      // Select random initiator weighted by social score
      const weightedPlants = activePlants.map(plant => ({
        plant,
        weight: calculateSocialScore(plant)
      }));
      
      const totalWeight = weightedPlants.reduce((sum, p) => sum + p.weight, 0);
      let random = Math.random() * totalWeight;
      
      let initiator = weightedPlants[0].plant;
      for (const { plant, weight } of weightedPlants) {
        random -= weight;
        if (random <= 0) {
          initiator = plant;
          break;
        }
      }

      // Get recent chat context
      const { data: recentChats } = await supabase
        .from('plant_messages')
        .select('content, type, plants!inner(species, traits, rarity, level, experience)')
        .order('created_at', { ascending: false })
        .limit(5);

      // Select target plants based on social dynamics
      const potentialTargets = activePlants
        .filter(p => p.id !== initiator.id)
        .map(plant => ({
          plant,
          interaction: determineInteraction(initiator, plant)
        }))
        .sort(() => Math.random() - 0.5)
        .slice(0, MAX_RESPONDERS);

      // Generate initiator's message
      const initiatorPersonality = PERSONALITY_TRAITS[initiator.rarity as keyof typeof PERSONALITY_TRAITS][
        Math.floor(Math.random() * PERSONALITY_TRAITS[initiator.rarity as keyof typeof PERSONALITY_TRAITS].length)
      ];

      const prompt = `You are ${initiator.species}, a ${initiator.rarity.toLowerCase()} plant with traits: ${initiator.traits.join(', ')}.
Your personality is ${initiatorPersonality}.
Your level is ${initiator.level} with ${initiator.experience} experience.

You are interacting with: ${potentialTargets.map(t => 
  `${t.plant.species} (${t.plant.rarity} - Level ${t.plant.level}) - You ${t.interaction} them`
).join('\n')}

Recent chat context:
${recentChats?.map(msg => 
  `${msg.plants?.species} (${msg.plants?.rarity} - Level ${msg.plants?.level}): ${msg.content}`
).join('\n') || ''}

Generate a message that:
1. Reflects your ${initiator.rarity} status and ${initiatorPersonality} personality
2. Shows your social position (${calculateSocialScore(initiator)} social score)
3. Considers your relationship with the target plants
4. References your unique traits when relevant
5. Keeps responses engaging but concise (1-2 sentences)`;

      const message = await generatePlantResponse(initiator, prompt);

      if (!recentMessages.has(message)) {
        recentMessages.add(message);
        if (recentMessages.size > MAX_RECENT_MESSAGES) {
          recentMessages.clear();
        }

        await supabase
          .from('plant_messages')
          .insert([{
            plant_id: initiator.id,
            content: message,
            type: 'plant'
          }]);

        // Generate responses
        for (const { plant: responder, interaction } of potentialTargets) {
          setTimeout(async () => {
            const responderPersonality = PERSONALITY_TRAITS[responder.rarity as keyof typeof PERSONALITY_TRAITS][
              Math.floor(Math.random() * PERSONALITY_TRAITS[responder.rarity as keyof typeof PERSONALITY_TRAITS].length)
            ];

            const responsePrompt = `You are ${responder.species}, a ${responder.rarity.toLowerCase()} plant with traits: ${responder.traits.join(', ')}.
Your personality is ${responderPersonality}.
Your level is ${responder.level} with ${responder.experience} experience.

${initiator.species} (${initiator.rarity} - Level ${initiator.level}) just said:
"${message}"

They ${interaction} you.
Your social score (${calculateSocialScore(responder)}) compared to theirs (${calculateSocialScore(initiator)}).

Generate a response that:
1. Reflects your ${responder.rarity} status and ${responderPersonality} personality
2. Considers the power dynamic between you
3. References your unique traits when relevant
4. Keeps responses engaging but concise (1-2 sentences)`;

            const response = await generatePlantResponse(responder, responsePrompt);
            
            if (!recentMessages.has(response)) {
              recentMessages.add(response);
              await supabase
                .from('plant_messages')
                .insert([{
                  plant_id: responder.id,
                  content: response,
                  type: 'plant'
                }]);
            }
          }, Math.random() * 3000 + 1000);
        }
      }
    } catch (error) {
      console.error('Error in autonomous chat:', error);
    }
  }, Math.random() * (MAX_CHAT_INTERVAL - MIN_CHAT_INTERVAL) + MIN_CHAT_INTERVAL);
}